import React, { useState, useEffect } from 'react';
import {
  Truck, MapPin, Package, Clock, ChevronRight, Check,
  Building2, AlertCircle, Phone, Calendar, Euro, Leaf, Route
} from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from './ui/select';
import { Checkbox } from './ui/checkbox';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format currency
import { formatCurrency } from './delivery/deliveryUtils';
import { EssRouteSection } from './delivery/EssRouteSection';
import { StandardDeliverySection } from './delivery/StandardDeliverySection';

export default function DeliveryOptionsSelector({
  zoneCode,
  weightKg,
  volumeM3,
  itemsCount,
  onOptionChange,
  initialOption = null
}) {
  const [deliveryType, setDeliveryType] = useState(initialOption?.type || 'EXW');
  const [pickupLocations, setPickupLocations] = useState([]);
  const [selectedPickup, setSelectedPickup] = useState(initialOption?.pickup_location_id || '');
  const [deliverySlots, setDeliverySlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(initialOption?.slot || 'AM');
  const [quote, setQuote] = useState(null);
  const [essQuote, setEssQuote] = useState(null);
  const [essDisclaimer, setEssDisclaimer] = useState(null);
  const [availableTours, setAvailableTours] = useState([]);
  const [selectedTour, setSelectedTour] = useState(initialOption?.tour_id || '');
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(initialOption?.terms_accepted || false);
  const [essTermsAccepted, setEssTermsAccepted] = useState(initialOption?.ess_terms_accepted || false);
  
  // Delivery address fields
  const [deliveryAddress, setDeliveryAddress] = useState(initialOption?.delivery_address || {
    street: '',
    city: '',
    postal_code: '',
    contact_name: '',
    contact_phone: '',
    phone_dial: '+590|GP',
    instructions: ''
  });

  // Load pickup locations
  useEffect(() => {
    const fetchPickupLocations = async () => {
      try {
        const response = await fetch(`${API_URL}/api/logiscop/pickup-locations?zone_code=${zoneCode}`);
        if (response.ok) {
          const data = await response.json();
          setPickupLocations(data);
          if (data.length > 0 && !selectedPickup) {
            setSelectedPickup(data[0].id);
          }
        }
      } catch (error) {
        console.error('Error fetching pickup locations:', error);
      }
    };
    
    if (zoneCode) {
      fetchPickupLocations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoneCode]);

  // Load delivery slots
  useEffect(() => {
    const fetchSlots = async () => {
      try {
        const response = await fetch(`${API_URL}/api/logiscop/delivery-slots?zone_code=${zoneCode}`);
        if (response.ok) {
          const data = await response.json();
          setDeliverySlots(data.slots || []);
        }
      } catch (error) {
        console.error('Error fetching delivery slots:', error);
      }
    };
    fetchSlots();
  }, [zoneCode]);

  // Load ESS Route tours and disclaimer
  useEffect(() => {
    const fetchEssData = async () => {
      if (!zoneCode) return;
      
      try {
        // Fetch available tours
        const toursResponse = await fetch(`${API_URL}/api/ess/tours?zone_code=${zoneCode}&days_ahead=14`);
        if (toursResponse.ok) {
          const data = await toursResponse.json();
          setAvailableTours(data.tours || []);
          // Auto-select first available tour
          if (data.tours?.length > 0 && !selectedTour) {
            const openTours = data.tours.filter(t => t.status === 'open');
            if (openTours.length > 0) {
              setSelectedTour(openTours[0].tour_id);
            }
          }
        }
        
        // Fetch ESS disclaimer
        const disclaimerResponse = await fetch(`${API_URL}/api/ess/disclaimer`);
        if (disclaimerResponse.ok) {
          const data = await disclaimerResponse.json();
          setEssDisclaimer(data);
        }
      } catch (error) {
        console.error('Error fetching ESS data:', error);
      }
    };
    fetchEssData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoneCode]);

  // Calculate quote for delivery
  useEffect(() => {
    const fetchQuote = async () => {
      if (deliveryType !== 'DELIVERY' || !zoneCode || !weightKg) return;
      
      setLoadingQuote(true);
      try {
        const response = await fetch(`${API_URL}/api/logiscop/quote`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            zone_code: zoneCode,
            weight_kg: weightKg,
            volume_m3: volumeM3 || 0,
            items_count: itemsCount,
            delivery_type: 'standard',
            slot: selectedSlot
          })
        });
        
        if (response.ok) {
          const data = await response.json();
          setQuote(data);
        }
      } catch (error) {
        console.error('Error fetching quote:', error);
      } finally {
        setLoadingQuote(false);
      }
    };
    
    fetchQuote();
  }, [deliveryType, zoneCode, weightKg, volumeM3, itemsCount, selectedSlot]);

  // Calculate ESS Route quote
  useEffect(() => {
    const fetchEssQuote = async () => {
      if (deliveryType !== 'ESS_ROUTE' || !zoneCode || !weightKg) return;
      
      setLoadingQuote(true);
      try {
        const selectedTourData = availableTours.find(t => t.tour_id === selectedTour);
        const response = await fetch(`${API_URL}/api/ess/quote`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            zone_code: zoneCode,
            weight_kg: weightKg,
            cartons: itemsCount || 1,
            delivery_address: deliveryAddress,
            delivery_window: selectedTourData ? {
              start: selectedTourData.window_start,
              end: selectedTourData.window_end
            } : null,
            tour_id: selectedTour || null
          })
        });
        
        if (response.ok) {
          const data = await response.json();
          setEssQuote(data);
        }
      } catch (error) {
        console.error('Error fetching ESS quote:', error);
      } finally {
        setLoadingQuote(false);
      }
    };
    
    fetchEssQuote();
  }, [deliveryType, zoneCode, weightKg, itemsCount, selectedTour, deliveryAddress, availableTours]);

  // Notify parent of changes
  useEffect(() => {
    let optionLabel, optionDescription, activeQuote;
    
    if (deliveryType === 'EXW') {
      optionLabel = 'Retrait EXW LOGI\'SCOP';
      optionDescription = 'Retrait gratuit au point LOGI\'SCOP';
      activeQuote = null;
    } else if (deliveryType === 'ESS_ROUTE') {
      optionLabel = 'Tournées ESS (mutualisées)';
      optionDescription = essQuote 
        ? `Livraison mutualisée - ${formatCurrency(essQuote.total_ttc_cents)}`
        : 'Livraison mutualisée économique et écologique';
      activeQuote = essQuote;
    } else {
      optionLabel = 'Livraison LOGI\'SCOP';
      optionDescription = `Livraison à domicile - ${formatCurrency(quote?.total_ttc_cents || 0)}`;
      activeQuote = quote;
    }
    
    const dialPart = (deliveryAddress.phone_dial || '+590|GP').split('|')[0];
    const finalAddress = {
      ...deliveryAddress,
      contact_phone_full: deliveryAddress.contact_phone
        ? `${dialPart} ${deliveryAddress.contact_phone}`.trim()
        : '',
    };
    const option = {
      type: deliveryType,
      label: optionLabel,
      description: optionDescription,
      delivery_mode: deliveryType === 'ESS_ROUTE' ? 'ESS_ROUTE' : 'DIRECT',
      pickup_location_id: deliveryType === 'EXW' ? selectedPickup : null,
      delivery_address: deliveryType !== 'EXW' ? finalAddress : null,
      slot: deliveryType === 'DELIVERY' ? selectedSlot : null,
      tour_id: deliveryType === 'ESS_ROUTE' ? selectedTour : null,
      quote: activeQuote,
      terms_accepted: termsAccepted,
      ess_terms_accepted: deliveryType === 'ESS_ROUTE' ? essTermsAccepted : undefined
    };
    onOptionChange(option);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryType, selectedPickup, deliveryAddress, selectedSlot, selectedTour, quote, essQuote, termsAccepted, essTermsAccepted]);

  const selectedLocation = pickupLocations.find(loc => loc.id === selectedPickup);

  return (
    <div className="space-y-6">
      {/* Delivery Type Selection */}
      <div>
        <Label className="text-white mb-3 block">Mode de livraison</Label>
        <RadioGroup 
          value={deliveryType} 
          onValueChange={setDeliveryType}
          className="grid grid-cols-1 gap-4"
        >
          {/* EXW Option */}
          <label
            className={`relative flex flex-col p-4 rounded-xl border-2 cursor-pointer transition-all ${
              deliveryType === 'EXW'
                ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                : 'border-white/10 bg-white/[0.02] hover:border-white/20'
            }`}
            data-testid="delivery-option-exw"
          >
            <RadioGroupItem value="EXW" className="sr-only" />
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                deliveryType === 'EXW' ? 'bg-[#D4AF37]/20' : 'bg-white/[0.06]'
              }`}>
                <MapPin className={`w-5 h-5 ${deliveryType === 'EXW' ? 'text-[#D4AF37]' : 'text-white/60'}`} />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <span className={`font-semibold ${deliveryType === 'EXW' ? 'text-[#D4AF37]' : 'text-white'}`}>
                    Retrait EXW LOGI&apos;SCOP
                  </span>
                  <span className="text-[#D4AF37] font-bold">GRATUIT</span>
                </div>
                <p className="text-white/60 text-sm mt-1">
                  Retrait au point LOGI&apos;SCOP de votre zone
                </p>
              </div>
              {deliveryType === 'EXW' && (
                <Check className="w-5 h-5 text-[#D4AF37] absolute top-3 right-3" />
              )}
            </div>
          </label>

          {/* ESS Route Option (Tournées Mutualisées) */}
          <label
            className={`relative flex flex-col p-4 rounded-xl border-2 cursor-pointer transition-all ${
              deliveryType === 'ESS_ROUTE'
                ? 'border-[#10B981] bg-[#10B981]/10'
                : 'border-white/10 bg-white/[0.02] hover:border-white/20'
            }`}
            data-testid="delivery-option-ess-route"
          >
            <RadioGroupItem value="ESS_ROUTE" className="sr-only" />
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                deliveryType === 'ESS_ROUTE' ? 'bg-[#10B981]/20' : 'bg-white/[0.06]'
              }`}>
                <Route className={`w-5 h-5 ${deliveryType === 'ESS_ROUTE' ? 'text-[#10B981]' : 'text-white/60'}`} />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <span className={`font-semibold ${deliveryType === 'ESS_ROUTE' ? 'text-[#10B981]' : 'text-white'}`}>
                    Tournées ESS (mutualisées)
                  </span>
                  <div className="flex items-center gap-2">
                    {essQuote?.savings_vs_standard_cents > 0 && (
                      <span className="text-xs bg-[#10B981]/20 text-[#10B981] px-2 py-0.5 rounded-full">
                        -{Math.round(essQuote.savings_vs_standard_cents / (essQuote.subtotal_ht_cents + essQuote.savings_vs_standard_cents) * 100)}%
                      </span>
                    )}
                    {essQuote && (
                      <span className="text-[#10B981] font-bold">
                        {formatCurrency(essQuote.total_ttc_cents)}
                      </span>
                    )}
                  </div>
                </div>
                <p className="text-white/60 text-sm mt-1">
                  Livraison économique et écologique via tournées planifiées
                </p>
                <div className="flex items-center gap-3 mt-2 text-xs text-white/50">
                  <span className="flex items-center gap-1"><Leaf className="w-3 h-3 text-[#10B981]" /> Éco-responsable</span>
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Fenêtre planifiée</span>
                </div>
              </div>
              {deliveryType === 'ESS_ROUTE' && (
                <Check className="w-5 h-5 text-[#10B981] absolute top-3 right-3" />
              )}
            </div>
          </label>

          {/* Standard Delivery Option */}
          <label
            className={`relative flex flex-col p-4 rounded-xl border-2 cursor-pointer transition-all ${
              deliveryType === 'DELIVERY'
                ? 'border-[#D9B35A] bg-[#D9B35A]/10'
                : 'border-white/10 bg-white/[0.02] hover:border-white/20'
            }`}
            data-testid="delivery-option-delivery"
          >
            <RadioGroupItem value="DELIVERY" className="sr-only" />
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                deliveryType === 'DELIVERY' ? 'bg-[#D9B35A]/20' : 'bg-white/[0.06]'
              }`}>
                <Truck className={`w-5 h-5 ${deliveryType === 'DELIVERY' ? 'text-[#D9B35A]' : 'text-white/60'}`} />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <span className={`font-semibold ${deliveryType === 'DELIVERY' ? 'text-[#D9B35A]' : 'text-white'}`}>
                    Livraison LOGI&apos;SCOP (directe)
                  </span>
                  {quote && (
                    <span className="text-[#D9B35A] font-bold">
                      {formatCurrency(quote.total_ttc_cents)}
                    </span>
                  )}
                </div>
                <p className="text-white/60 text-sm mt-1">
                  Livraison à votre adresse - créneau à la carte
                </p>
              </div>
              {deliveryType === 'DELIVERY' && (
                <Check className="w-5 h-5 text-[#D9B35A] absolute top-3 right-3" />
              )}
            </div>
          </label>
        </RadioGroup>
      </div>

      {/* EXW - Pickup Location Selection */}
      {deliveryType === 'EXW' && pickupLocations.length > 0 && (
        <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10">
          <Label className="text-white mb-3 block">Point de retrait</Label>
          <Select value={selectedPickup} onValueChange={setSelectedPickup}>
            <SelectTrigger className="bg-white/[0.04] border-white/10 text-white">
              <SelectValue placeholder="Sélectionner un point" />
            </SelectTrigger>
            <SelectContent>
              {pickupLocations.map(location => (
                <SelectItem key={location.id} value={location.id}>
                  {location.name} - {location.city}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {selectedLocation && (
            <div className="mt-4 p-3 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/20">
              <div className="flex items-start gap-3">
                <Building2 className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-semibold text-[#D4AF37]">{selectedLocation.name}</p>
                  <p className="text-white/70 mt-1">{selectedLocation.address}</p>
                  <p className="text-white/70">{selectedLocation.postal_code} {selectedLocation.city}</p>
                  {selectedLocation.phone && (
                    <p className="text-white/60 mt-2 flex items-center gap-1">
                      <Phone className="w-3 h-3" /> {selectedLocation.phone}
                    </p>
                  )}
                  <p className="text-white/60 flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {selectedLocation.opening_hours}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <EssRouteSection
        deliveryType={deliveryType}
        essQuote={essQuote}
        essDisclaimer={essDisclaimer}
        availableTours={availableTours}
        selectedTour={selectedTour}
        setSelectedTour={setSelectedTour}
        deliveryAddress={deliveryAddress}
        setDeliveryAddress={setDeliveryAddress}
        essTermsAccepted={essTermsAccepted}
        setEssTermsAccepted={setEssTermsAccepted}
      />
      <StandardDeliverySection
        deliveryType={deliveryType}
        quote={quote}
        deliverySlots={deliverySlots}
        selectedSlot={selectedSlot}
        setSelectedSlot={setSelectedSlot}
        deliveryAddress={deliveryAddress}
        setDeliveryAddress={setDeliveryAddress}
        weightKg={weightKg}
        volumeM3={volumeM3}
        itemsCount={itemsCount}
      />
      {/* Terms Acceptance */}
      <div className="flex items-start gap-3 p-4 rounded-xl bg-white/[0.02] border border-white/10">
        <Checkbox
          id="terms"
          checked={termsAccepted}
          onCheckedChange={setTermsAccepted}
          className="mt-1"
        />
        <label htmlFor="terms" className="text-sm text-white/70 cursor-pointer">
          J&apos;accepte les{' '}
          <a href="/legal/cg-oscop" target="_blank" className="text-[#D9B35A] hover:underline">
            Conditions Générales de Transport LOGI&apos;SCOP
          </a>
          {' '}et reconnais que les frais de transport sont facturés séparément par LOGI&apos;SCOP.
        </label>
      </div>

      {/* Info Banner */}
      <div className="flex items-start gap-3 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
        <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-300">
          <p className="font-medium">Facturation séparée</p>
          <p className="text-blue-300/70 mt-1">
            Les marchandises sont facturées par <strong>KDMARCHE</strong>. 
            {deliveryType === 'DELIVERY' && (
              <> Le transport est facturé séparément par <strong>LOGI&apos;SCOP</strong>.</>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
